import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  ArrowRight,
  GraduationCap,
  Baby,
  Network,
  Trophy,
  Globe2,
  Handshake,
  TrendingUp,
  CheckCircle2,
} from "lucide-react";
import { Reveal, SectionLabel } from "@/components/Reveal";
import { brands } from "@/data/brands";
import { leadership, team } from "@/data/team";
import { Seo } from "@/components/Seo";

const HERO_IMG =
  "https://images.unsplash.com/photo-1542744173-8e7e53415bb0?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";
const NETWORK_IMG =
  "https://images.unsplash.com/photo-1542744095-fcf48d80b0fd?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

const sectorIcons = { GraduationCap, Baby, Network, Trophy };

const sectors = [
  { title: "Coaching, Training & Consulting", icon: "GraduationCap", desc: "World-class coaching and professional development franchises." },
  { title: "Child Experience & Education", icon: "Baby", desc: "Enriching learning and experiential concepts for children." },
  { title: "Franchise & Business Consulting", icon: "Network", desc: "Networking and business-building franchise systems." },
  { title: "Sports Education", icon: "Trophy", desc: "Skill-based sports and physical development programs." },
];

export default function Home() {
  return (
    <div data-testid="home-page">
      <Seo
        title="Home"
        description="Franchise India Knowledge Services is the world's only franchise consultancy focused on knowledge-based businesses — coaching, education, child experience and sports. We make brands franchise-ready and help them scale globally."
        path="/"
      />
      {/* HERO */}
      <section className="relative overflow-hidden bg-canvas">
        <div className="max-w-7xl mx-auto px-6 md:px-12 pt-16 pb-20 md:pt-24 md:pb-28">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6 }}
              >
                <span className="inline-flex items-center gap-2 bg-white border border-navy/10 px-4 py-2 text-xs uppercase tracking-[0.2em] font-semibold text-navy">
                  <span className="h-2 w-2 rounded-full bg-gold" />
                  A Franchise India Group Company
                </span>
              </motion.div>

              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.1 }}
                className="mt-6 text-4xl sm:text-5xl lg:text-6xl leading-[1.05] tracking-tight font-bold text-navy text-balance"
              >
                Building the future of{" "}
                <span className="text-teal">knowledge-based</span> franchising.
              </motion.h1>

              <motion.p
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.2 }}
                className="mt-6 text-lg md:text-xl text-ink-secondary leading-relaxed max-w-xl"
              >
                We are the world's only franchise consultancy focused exclusively
                on knowledge businesses — coaching, education, child experience
                and sports — making them franchise-ready and scaling them across
                global markets.
              </motion.p>

              <motion.div
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.7, delay: 0.3 }}
                className="mt-9 flex flex-col sm:flex-row gap-4"
              >
                <Link
                  to="/contact"
                  data-testid="hero-primary-cta"
                  className="inline-flex items-center justify-center gap-2 bg-navy text-white px-8 py-4 font-semibold tracking-wide hover:bg-navy-light transition-colors duration-300 group"
                >
                  Partner With Us
                  <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
                </Link>
                <Link
                  to="/brands"
                  data-testid="hero-secondary-cta"
                  className="inline-flex items-center justify-center gap-2 border border-navy text-navy px-8 py-4 font-semibold tracking-wide hover:bg-navy hover:text-white transition-colors duration-300"
                >
                  View Our Brands
                </Link>
              </motion.div>

              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ duration: 0.7, delay: 0.5 }}
                className="mt-12 flex flex-wrap gap-x-10 gap-y-4"
              >
                {[
                  { n: "15+", l: "Global Brands" },
                  { n: "5+", l: "Continents" },
                  { n: "5", l: "Growth Sectors" },
                ].map((s) => (
                  <div key={s.l}>
                    <div className="text-3xl font-bold text-navy font-heading">{s.n}</div>
                    <div className="text-sm text-ink-muted uppercase tracking-wider">{s.l}</div>
                  </div>
                ))}
              </motion.div>
            </div>

            <div className="lg:col-span-6">
              <motion.div
                initial={{ opacity: 0, scale: 0.96 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.8, delay: 0.2 }}
                className="relative"
              >
                <div className="absolute -top-4 -left-4 h-24 w-24 bg-gold z-0" />
                <div className="absolute -bottom-4 -right-4 h-24 w-24 bg-teal z-0" />
                <img
                  src={HERO_IMG}
                  alt="Business coaching seminar"
                  className="relative z-10 w-full h-[420px] md:h-[520px] object-cover"
                />
              </motion.div>
            </div>
          </div>
        </div>
      </section>

      {/* INTRO STATEMENT */}
      <section className="bg-navy noise-overlay text-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <SectionLabel className="text-gold">Our Mandate</SectionLabel>
            <h2 className="mt-6 text-3xl md:text-4xl lg:text-5xl font-semibold leading-snug text-balance">
              We help professionals set up profitable, scalable businesses in the
              spaces they are most passionate about.
            </h2>
            <p className="mt-8 text-lg text-white/70 leading-relaxed max-w-3xl mx-auto">
              From coaching, training and consulting to child education, child
              experience, franchise consulting and sports education — we bring
              world-class knowledge brands to entrepreneurs across emerging markets.
            </p>
          </Reveal>
        </div>
      </section>

      {/* SECTORS */}
      <section className="py-20 md:py-32 bg-canvas">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel>What We Specialise In</SectionLabel>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-semibold text-navy max-w-2xl">
              Four sectors. One relentless focus on knowledge.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {sectors.map((s, i) => {
              const Icon = sectorIcons[s.icon];
              return (
                <Reveal key={s.title} delay={i * 0.08}>
                  <div className="group bg-white border border-navy/10 p-8 h-full hover:-translate-y-1 hover:shadow-xl transition-all duration-300">
                    <div className="h-14 w-14 flex items-center justify-center bg-navy text-gold group-hover:bg-teal transition-colors duration-300">
                      <Icon size={26} />
                    </div>
                    <h3 className="mt-6 text-xl font-semibold text-navy">{s.title}</h3>
                    <p className="mt-3 text-ink-secondary leading-relaxed">{s.desc}</p>
                  </div>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      {/* SERVICES OVERVIEW */}
      <section className="py-20 md:py-32 bg-canvas-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <SectionLabel>How We Work</SectionLabel>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-semibold text-navy">
              Two ways we power your growth.
            </h2>
          </Reveal>

          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-8">
            {[
              {
                icon: TrendingUp,
                tag: "01 — Consulting",
                title: "Scaling & Franchise Readiness",
                desc:
                  "We work with knowledge-based businesses to make them franchise-ready and expand them across fast-growing global markets.",
                points: ["Franchise model design", "Market-entry strategy", "Global expansion"],
              },
              {
                icon: Handshake,
                tag: "02 — Brokering",
                title: "Partner Recruitment",
                desc:
                  "We connect proven brands with the right franchise partners — sourcing, qualifying and onboarding investors who share the vision.",
                points: ["Partner sourcing & matching", "Due diligence support", "Seamless onboarding"],
              },
            ].map((svc, i) => (
              <Reveal key={svc.title} delay={i * 0.12}>
                <div className="bg-white border border-navy/10 p-10 h-full">
                  <div className="flex items-center gap-4">
                    <div className="h-12 w-12 flex items-center justify-center bg-teal/10 text-teal">
                      <svc.icon size={24} />
                    </div>
                    <span className="text-sm uppercase tracking-[0.2em] font-semibold text-ink-muted">
                      {svc.tag}
                    </span>
                  </div>
                  <h3 className="mt-6 text-2xl font-semibold text-navy">{svc.title}</h3>
                  <p className="mt-4 text-ink-secondary leading-relaxed">{svc.desc}</p>
                  <ul className="mt-6 space-y-3">
                    {svc.points.map((p) => (
                      <li key={p} className="flex items-center gap-3 text-navy">
                        <CheckCircle2 size={18} className="text-gold shrink-0" />
                        <span>{p}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.2}>
            <div className="mt-10 text-center">
              <Link
                to="/services"
                data-testid="home-services-link"
                className="inline-flex items-center gap-2 text-navy font-semibold border-b-2 border-gold pb-1 hover:gap-3 transition-all"
              >
                Explore our services <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>
        </div>
      </section>

      {/* MARKETS */}
      <section className="py-20 md:py-32 bg-canvas">
        <div className="max-w-7xl mx-auto px-6 md:px-12 grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="relative">
              <img src={NETWORK_IMG} alt="Global network" className="w-full h-[440px] object-cover" />
              <div className="absolute -bottom-6 -left-6 bg-navy text-white p-8 max-w-xs hidden md:block">
                <Globe2 className="text-gold" size={28} />
                <p className="mt-3 font-semibold text-lg leading-snug">
                  Emerging markets, unlimited potential.
                </p>
              </div>
            </div>
          </Reveal>
          <Reveal delay={0.1}>
            <SectionLabel>Our Markets</SectionLabel>
            <h2 className="mt-4 text-3xl md:text-4xl lg:text-5xl font-semibold text-navy">
              Built for global markets.
            </h2>
            <p className="mt-6 text-lg text-ink-secondary leading-relaxed">
              We partner with knowledge brands to expand across global markets —
              wherever there's a rising appetite for education, skills and
              entrepreneurship. We specialise in bringing established brands into
              new territories and scaling them responsibly.
            </p>
            <div className="mt-8 grid grid-cols-2 gap-6">
              {[
                { t: "Global Reach", d: "Cross-border expansion" },
                { t: "Local Expertise", d: "On-ground execution" },
              ].map((m) => (
                <div key={m.t} className="border-l-4 border-teal pl-4">
                  <div className="text-2xl font-bold text-navy font-heading">{m.t}</div>
                  <div className="text-sm text-ink-muted">{m.d}</div>
                </div>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* BRANDS STRIP */}
      <section className="py-20 md:py-28 bg-canvas-alt">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <SectionLabel>Brands We Represent</SectionLabel>
                <h2 className="mt-4 text-3xl md:text-4xl font-semibold text-navy">
                  Trusted by world-class names.
                </h2>
              </div>
              <Link
                to="/brands"
                data-testid="home-brands-link"
                className="inline-flex items-center gap-2 text-navy font-semibold hover:gap-3 transition-all shrink-0"
              >
                See all brands <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>

          <div className="mt-12 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
            {brands.slice(0, 10).map((b, i) => (
              <Reveal key={b.name} delay={i * 0.05}>
                <div className="bg-white border border-navy/10 h-28 flex items-center justify-center p-5 hover:shadow-lg transition-shadow duration-300">
                  {b.logo ? (
                    <img src={b.logo} alt={b.name} className="max-h-14 max-w-full object-contain" />
                  ) : (
                    <span className="text-navy font-semibold text-center text-sm">{b.name}</span>
                  )}
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* TEAM TEASER */}
      <section className="py-20 md:py-32 bg-canvas">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <Reveal>
            <div className="flex flex-col md:flex-row md:items-end justify-between gap-6">
              <div>
                <SectionLabel>Leadership</SectionLabel>
                <h2 className="mt-4 text-3xl md:text-4xl font-semibold text-navy">
                  Led by industry veterans.
                </h2>
              </div>
              <Link
                to="/team"
                data-testid="home-team-link"
                className="inline-flex items-center gap-2 text-navy font-semibold hover:gap-3 transition-all shrink-0"
              >
                Meet the team <ArrowRight size={18} />
              </Link>
            </div>
          </Reveal>

          <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-8">
            {leadership.map((l, i) => (
              <Reveal key={l.name} delay={i * 0.1}>
                <div className="bg-navy text-white p-10 flex items-start gap-6 h-full">
                  <div className="h-16 w-16 shrink-0 overflow-hidden bg-gold text-navy text-xl font-bold font-heading flex items-center justify-center">
                    {l.photo ? (
                      <img src={l.photo} alt={l.name} className="h-full w-full object-cover object-top" />
                    ) : (
                      l.initials
                    )}
                  </div>
                  <div>
                    <h3 className="text-2xl font-semibold">{l.name}</h3>
                    <p className="text-gold text-sm uppercase tracking-wider mt-1">{l.role}</p>
                    <p className="mt-4 text-white/70 leading-relaxed">{l.bio}</p>
                  </div>
                </div>
              </Reveal>
            ))}
          </div>
          <p className="mt-8 text-ink-muted text-center">
            Supported by a dedicated team of {team.length} brand, consulting and operations specialists.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-navy-dark text-white py-20 md:py-28">
        <div className="max-w-5xl mx-auto px-6 md:px-12 text-center">
          <Reveal>
            <h2 className="text-3xl md:text-5xl font-semibold leading-tight text-balance">
              Ready to franchise your knowledge business?
            </h2>
            <p className="mt-6 text-lg text-white/70 max-w-2xl mx-auto">
              Whether you're a brand looking to expand or an entrepreneur seeking a
              proven opportunity — let's start the conversation.
            </p>
            <Link
              to="/contact"
              data-testid="home-cta-button"
              className="mt-10 inline-flex items-center gap-2 bg-gold text-navy px-10 py-4 font-bold tracking-wide hover:bg-yellow-400 transition-colors duration-300 group"
            >
              Get In Touch
              <ArrowRight size={18} className="group-hover:translate-x-1 transition-transform" />
            </Link>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
